import React, { useState, useCallback } from 'react';
import { Book, Flame, Settings, LogOut, Menu, X, MessageSquare, Heart } from 'lucide-react';
import { useAuth } from '../components/auth/AuthProvider';
import { ViewType } from '../types';

interface NavigationProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
}

interface NavItemProps {
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
  active: boolean;
}

export default function Navigation({ currentView, onViewChange }: NavigationProps) {
  const { signOut, user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  if (!user) return null;

  const handleViewChange = useCallback((view: ViewType) => {
    onViewChange(view);
    setIsMobileMenuOpen(false);
  }, [onViewChange]);

  const handleSignOut = useCallback(() => {
    setIsMobileMenuOpen(false);
    signOut();
  }, [signOut]);

  const navItems = [
    { icon: <Book className="h-5 w-5" />, text: 'Journal', view: 'journal' as const },
    { icon: <MessageSquare className="h-5 w-5" />, text: 'Guided Chat', view: 'guided-chat' as const },
    { icon: <Heart className="h-5 w-5" />, text: 'Parts Family', view: 'parts' as const },
    { icon: <Settings className="h-5 w-5" />, text: 'Settings', view: 'settings' as const }
  ];

  return (
    <nav className="bg-gradient-to-br from-midnight-900 to-midnight-800 backdrop-blur-md border-b border-midnight-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <Flame className="h-6 w-6 text-amber-500" />
            <span className="text-xl font-bold bg-gradient-to-r from-amber-400 to-marigold-400 bg-clip-text text-transparent">
              Shadow Worker℠
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map(({ icon, text, view }) => (
              <NavItem
                key={view}
                icon={icon}
                text={text}
                onClick={() => handleViewChange(view)}
                active={currentView === view}
              />
            ))}
            <button
              onClick={handleSignOut}
              className="flex items-center space-x-2 px-4 py-2 text-midnight-50 hover:text-amber-400 hover:bg-midnight-700/50 transition-all rounded-lg"
            >
              <LogOut className="h-5 w-5" />
              <span>Sign Out</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-midnight-700/50 transition-colors"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-midnight-50" />
            ) : (
              <Menu className="h-6 w-6 text-midnight-50" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-midnight-800 border-t border-midnight-700">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map(({ icon, text, view }) => (
              <MobileNavItem
                key={view}
                icon={icon}
                text={text}
                onClick={() => handleViewChange(view)}
                active={currentView === view}
              />
            ))}
            <button
              onClick={handleSignOut}
              className="w-full flex items-center space-x-2 px-4 py-2 text-base font-medium rounded-lg text-midnight-50 hover:text-amber-400 hover:bg-midnight-700/50 transition-all"
            >
              <LogOut className="h-5 w-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

const NavItem = React.memo(function NavItem({ icon, text, onClick, active }: NavItemProps) {
  return (
    <button 
      onClick={onClick}
      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all
        ${active 
          ? 'bg-amber-500/10 text-amber-400' 
          : 'text-midnight-50 hover:text-amber-400 hover:bg-midnight-700/50'}`}
    >
      {icon}
      <span>{text}</span>
    </button>
  );
});

const MobileNavItem = React.memo(function MobileNavItem({ icon, text, onClick, active }: NavItemProps) {
  return (
    <button 
      onClick={onClick}
      className={`w-full flex items-center space-x-2 px-3 py-2 text-base font-medium rounded-lg transition-all
        ${active 
          ? 'bg-amber-500/10 text-amber-400' 
          : 'text-midnight-50 hover:text-amber-400 hover:bg-midnight-700/50'}`}
    >
      {icon}
      <span>{text}</span>
    </button>
  );
});